import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, matchPath, useLocation } from 'react-router-dom';
import './App.css';
import { apiService } from './components/Config/api.service';

import CreateUserCompany from './pages/Connexion/Inscription';
import Login from './pages/Connexion/Login';
import Deconnexion from './pages/Connexion/Deconnexion';
import MotDePasseOublie from './pages/MotDePasseOublie/MotDePasseOublie';
import Reinitialisation from './pages/MotDePasseOublie/Reinitialisation';
import CountrySelect from './pages/Connexion/CountrySelector';


// import Header from './components/Header/Header';
import SidebarNav from './components/SideBar/SidebarNav';
// import Footer from './components/Footer/Foot';

// Importez le nouveau composant LogoutModal
import LogoutModal from './pages/Connexion/LogoutModal';

import Home from './pages/Home/Home';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
// import Calendar from './pages/Calendar/Calendar';

import Employees from './pages/Employee/Employees';
import EmployeeNew from './pages/Employee/EmployeeNew';
import Employee from './pages/Employee/EmployeeDetails';
import Contracts from './pages/Employee/Contract/Contracts';


import Primes from './pages/Primes/Primes';
// import Prime from './pages/Primes/PrimeDetails';

import Paie from './pages/Paie/Paie';
import DailySalaryInputs from './pages/Paie/DailySalaryInputs';
import Payslips from './pages/Paie/Payslip/Payslips'; 
import PayslipsReport from './pages/Paie/Payslip/PayslipReport';


import FinancialStatements from './pages/FinancialStatements/FinancialStatements';

import Hsp from './pages/Leave/Hsp/Hsp';
import TimeManagement from './pages/Leave/Leave';
import Absence from './pages/Leave/Absence/Absence';
import { NotificationContainer } from 'react-notifications';
// import HeaderProfile from './components/Header/HeaderProfile';
import Settings from './pages/Settings/Settings';
import GuideUtilisateur from './pages/Guide/GuideUtilisateur';
import { canAccessSettings, canEditHrData, canViewEmployees, canViewFinancialStatements } from './components/Auth/permissions';
import Forbidden from './pages/Forbidden/Forbidden';
import NotFound from './pages/NotFound/NotFound';

const protectedRoutePatterns = [
  '/',
  '/settings',
  '/guide',
  '/employees',
  '/employees/new',
  '/employees/:id',
  '/primes',
  '/etats',
  '/hsp',
  '/absence',
  '/leave',
  '/paie',
  '/paie/journaliers',
  '/contracts',
  '/payslips',
  '/payslip/report',
];

const isProtectedPath = (pathname) =>
  protectedRoutePatterns.some((path) => matchPath({ path, end: true }, pathname));

const RedirectToLogin = () => {
  const location = useLocation();
  const from = {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  };

  return <Navigate to="/login" replace state={{ from }} />;
};

const UnauthenticatedFallback = () => {
  const location = useLocation();

  if (isProtectedPath(location.pathname)) {
    return <RedirectToLogin />;
  }

  return <NotFound />;
};



function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  // Statut d'authentification dérivé d'une validation serveur, jamais du
  // localStorage seul. 'checking' | 'authenticated' | 'unauthenticated'
  const [authStatus, setAuthStatus] = useState('checking');
  const isConnected = authStatus === 'authenticated';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    // Charger l'état de la sidebar depuis localStorage
    const sidebarState = localStorage.getItem('sidebarCollapsed');
    if (sidebarState) {
      setIsSidebarCollapsed(sidebarState === 'true');
    }

    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    // localStorage ne sert que d'indice de cache : sans indice de session on
    // n'interroge pas le serveur (et on évite une boucle de redirection /login).
    const hasSessionHint = localStorage.getItem('isConnected') === 'true';
    if (!hasSessionHint) {
      setCurrentUser(null);
      setAuthStatus('unauthenticated');
      setIsUserLoading(false);
      return () => {
        isMounted = false;
      };
    }

    // La session est validée côté serveur avant d'afficher la moindre route
    // protégée. Le flag localStorage n'est jamais la source de vérité.
    apiService
      .getElement('/user')
      .then((response) => {
        if (!isMounted) return;
        const user = response?.data?.user;
        if (user && user.id) {
          setCurrentUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          setAuthStatus('authenticated');
        } else {
          localStorage.removeItem('isConnected');
          localStorage.removeItem('user');
          setCurrentUser(null);
          setAuthStatus('unauthenticated');
        }
        setIsUserLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        // Sur 401 l'interceptor gère déjà logout + redirection ; pour toute
        // autre erreur on refuse l'accès par défaut.
        localStorage.removeItem('isConnected');
        localStorage.removeItem('user');
        setCurrentUser(null);
        setAuthStatus('unauthenticated');
        setIsUserLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Surveiller les changements d'état de la sidebar
  useEffect(() => {
    const handleSidebarStateChange = () => {
      const sidebarState = localStorage.getItem('sidebarCollapsed');
      if (sidebarState) {
        setIsSidebarCollapsed(sidebarState === 'true');
      }
    };

    // Utiliser un écouteur d'événements de stockage pour détecter les changements
    window.addEventListener('storage', handleSidebarStateChange);

    return () => {
      window.removeEventListener('storage', handleSidebarStateChange);
    };
  }, []);

  const handleLogoutRequest = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    apiService.logoutUser();
    setShowLogoutModal(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Garde d'accès par rôle : rien tant que l'utilisateur charge, puis la page ou 403.
  const guardRoute = (element, isAllowed) => {
    if (isUserLoading) return null;
    return currentUser && isAllowed(currentUser) ? element : <Forbidden />;
  };
  const settingsElement = guardRoute(<Settings />, canAccessSettings);

  return (
    <Router>
        {isConnected && <SidebarNav 
          isOpen={isSidebarOpen} 
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
          onLogoutRequest={handleLogoutRequest}
        />}
        <div className={`flex-1 p-5 bg-[var(--bg-color)] min-h-screen ${
          isConnected ? (
            isSidebarCollapsed ? 'ml-[70px]' : 'ml-[250px]'
          ) : 'ml-0'
        } lg:transition-all lg:duration-300`}>
          {/* if the user is connected */}
          {/* {isConnected && <HeaderProfile />}           */}
          <Routes>
            {authStatus === 'checking' ? (
              <Route
                path="*"
                element={
                  <div className="flex items-center justify-center min-h-screen">
                    <span className="animate-spin h-10 w-10 border-4 border-gray-200 border-t-orange-500 rounded-full"></span>
                  </div>
                }
              />
            ) : isConnected ? (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Home />} />
                {/* <Route path="/calendar" element={<Calendar />} /> */}
                <Route path="/403" element={<Forbidden />} />
                <Route path="/settings" element={settingsElement} />

                <Route path="/employees" element={guardRoute(<Employees />, canViewEmployees)} />
                <Route path="/employees/new" element={guardRoute(<EmployeeNew />, canEditHrData)} />
                <Route path="/employees/:id" element={guardRoute(<Employee />, canViewEmployees)} />

                <Route path="/primes" element={guardRoute(<Primes />, canEditHrData)} />

                <Route path='/etats' element={guardRoute(<FinancialStatements />, canViewFinancialStatements)} />

                <Route path="/hsp" element={guardRoute(<Hsp />, canEditHrData)} />
                <Route path="/absence" element={guardRoute(<Absence />, canEditHrData)} />                
                <Route path="/leave" element={guardRoute(<TimeManagement />, canEditHrData)} />

                <Route path="/paie" element={<Paie />} />
                <Route path="/paie/journaliers" element={guardRoute(<DailySalaryInputs />, canEditHrData)} />

                <Route path="/contracts" element={guardRoute(<Contracts />, canEditHrData)} />

                <Route path="/payslips" element={<Payslips />} />
                <Route path="/payslip/report" element={<PayslipsReport />} />
                <Route path="/guide" element={<GuideUtilisateur />} />
                <Route path="*" element={<NotFound isAuthenticated />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/countryselect" element={<CountrySelect />} />
                <Route path="/inscription" element={<Navigate to="/login" replace />} />
                <Route path="/403" element={<Forbidden />} />
                <Route path="*" element={<UnauthenticatedFallback />} />
              </>
            )}
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/deconnexion" element={<Deconnexion />} />
            <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
            <Route path="/reinitialisation/:jeton" element={<Reinitialisation />} />
            <Route path="/invitation/:jeton" element={<Reinitialisation mode="invitation" />} />
          </Routes>
          {/* <Footer /> */}
        </div>
        <NotificationContainer />

        {/* Utilisation du composant LogoutModal */}
        <LogoutModal 
          isOpen={showLogoutModal}
          onConfirm={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
        />
    </Router>
  );
}

export default App;
